// File: src/pages/ProfilePage.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MinimalFooter from '../components/MinimalFooter';

// Icons
import { FaInstagram, FaLinkedin } from 'react-icons/fa';
import { LuPencil, LuMail, LuCalendar, LuUser } from 'react-icons/lu';

// AuthContext for currentUser
import { AuthContext } from '../context/AuthContext';

/** Simple emoji map for display */
const interestEmojis = {
  Hiking: '🥾',
  Painting: '🎨',
  Coding: '💻',
  Music: '🎵',
  Art: '🖼️',
  Reading: '📚',
  Travel: '✈️',
  Photography: '📸',
  Gaming: '🎮',
  Cooking: '🍳',
  Yoga: '🧘',
  Dancing: '💃',
  Sports: '🏀',
  Movies: '🎬',
  Fitness: '🏋️',
  Meditation: '🧘',
  'Board Games': '🎲',
  Gardening: '🌱',
};

/** Extract the user’s handle from a full URL. */
function extractHandleFromUrl(platform, fullUrl = '') {
  const map = {
    instagram: 'https://instagram.com/',
    linkedin: 'https://www.linkedin.com/in/',
  };
  const prefix = map[platform] || '';
  if (fullUrl.startsWith(prefix)) return fullUrl.slice(prefix.length);
  return fullUrl;
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  const [user, setUser] = useState(null);
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [overview, setOverview] = useState('');
  const [interests, setInterests] = useState([]);
  const [socialHandles, setSocialHandles] = useState({
    instagram: '',
    linkedin: '',
  });
  const [profilePicUrl, setProfilePicUrl] = useState('/default-avatar.png');

  // owner check
  const isOwner = currentUser && user && String(user._id) === String(currentUser._id);

  // ---------- LOAD USER DATA ----------
  useEffect(() => {
    if (!currentUser?._id) return;
    (async () => {
      try {
        const resp = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/${currentUser._id}`
        );
        setUser(resp.data);
      } catch (err) {
        console.error('[ProfilePage] fetch error =>', err);
      }
    })();
  }, [currentUser]);

  // ---------- POPULATE FIELDS ----------
  useEffect(() => {
    if (!user) return;

    setGender(user.gender || '');
    setOverview(user.overview || '');
    setInterests(Array.isArray(user.interests) ? user.interests : []);

    // Age from DOB
    if (user.dateOfBirth) {
      const dob = new Date(user.dateOfBirth);
      const now = new Date();
      let userAge = now.getFullYear() - dob.getFullYear();
      const m = now.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) userAge--;
      setAge(userAge);
    } else {
      setAge('');
    }

    // Social handles (display-only)
    const igUrl = user.socials?.instagram || '';
    const lnUrl = user.socials?.linkedin || '';
    setSocialHandles({
      instagram: extractHandleFromUrl('instagram', igUrl),
      linkedin: extractHandleFromUrl('linkedin', lnUrl),
    });

    // Profile pic
    setProfilePicUrl(user?.profilePicUrl || '/default-avatar.png');
  }, [user]);

  // ===== Read-only profile rendering =====
  function renderReadOnlyView() {
    if (!user) return <p style={{ padding: 16 }}>Loading user…</p>;

    const name = `${user.firstName || ''} ${user.lastName ? user.lastName.charAt(0) + '.' : ''}`.trim();
    const hasSocials = Boolean(user.socials?.instagram || user.socials?.linkedin);

    // Build a simple list of sections; each will be separated by a hairline divider
    const sectionBlocks = [];

    // About (or nudge)
    if (overview) {
      sectionBlocks.push(
        <div key="about">
          <h2 style={ui.sectionLabel}>About</h2>
          <p style={ui.bodyText}>{overview}</p>
        </div>
      );
    } else if (isOwner) {
      sectionBlocks.push(
        <div key="about-nudge">
          <h2 style={ui.sectionLabel}>About</h2>
          <div style={ui.inlineNudge}>
            Add a short bio so people can get to know you.
            <button style={ui.inlineBtn} onClick={() => navigate('/profile/edit')}>Add bio</button>
          </div>
        </div>
      );
    }

    // Interests (or nudge)
    if (interests && interests.length > 0) {
      sectionBlocks.push(
        <div key="interests">
          <h2 style={ui.sectionLabel}>Interests</h2>
          <div style={ui.chipsWrap}>
            {interests.map((i) => (
              <span key={i} style={ui.chip}>
                {interestEmojis[i] ? `${interestEmojis[i]} ${i}` : i}
              </span>
            ))}
          </div>
        </div>
      );
    } else if (isOwner) {
      sectionBlocks.push(
        <div key="interests-nudge">
          <h2 style={ui.sectionLabel}>Interests</h2>
          <div style={ui.inlineNudge}>
            Share a few interests to help you connect.
            <button style={ui.inlineBtn} onClick={() => navigate('/profile/edit')}>Add interests</button>
          </div>
        </div>
      );
    }

    // Social (or nudge)
    if (hasSocials) {
      sectionBlocks.push(
        <div key="social">
          <h2 style={ui.sectionLabel}>Social</h2>
          {user.socials.instagram && (
            <a
              href={user.socials.instagram}
              target="_blank"
              rel="noopener noreferrer"
              style={ui.listRow}
            >
              <div style={ui.iconDot}><FaInstagram /></div>
              <div style={ui.rowText}>
                <div style={ui.rowTitle}>Instagram</div>
                <div style={ui.rowSub}>@{socialHandles.instagram || 'view'}</div>
              </div>
              <div style={ui.rowMeta}>Open</div>
            </a>
          )}

          {(user.socials.instagram && (user.socials.linkedin)) && <div style={ui.rowDivider} />}

      
          {user.socials.linkedin && (
            <a
              href={user.socials.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              style={ui.listRow}
            >
              <div style={ui.iconDot}><FaLinkedin /></div>
              <div style={ui.rowText}>
                <div style={ui.rowTitle}>LinkedIn</div>
                <div style={ui.rowSub}>{socialHandles.linkedin || 'View profile'}</div>
              </div>
              <div style={ui.rowMeta}>Open</div>
            </a>
          )}
        </div>
      );
    } else if (isOwner) {
      sectionBlocks.push(
        <div key="social-nudge">
          <h2 style={ui.sectionLabel}>Social</h2>
          <div style={ui.inlineNudge}>
            Link a social profile to make it easier to find you.
            <button style={ui.inlineBtn} onClick={() => navigate('/profile/edit')}>Add social</button>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* ===== Minimal Top Card (reordered: name → age•gender•email → edit) ===== */}
        <section style={ui.headerCard}>
          <div style={ui.headerRow}>
            <img
              src={profilePicUrl}
              alt="Profile"
              style={ui.headerAvatar}
              onError={(e) => { e.currentTarget.src = '/default-avatar.png'; }}
            />

            <div style={ui.headerTextCol}>
              {/* Name */}
              <div style={ui.nameRow}>
                <h1 style={ui.name}>{name || 'Unnamed User'}</h1>
              </div>

              {/* Age • Gender • Email (light grey, no icons, no container) */}
              {(age || gender || user.email) ? (
                <div style={ui.metaAG}>
                  {[age ? `${age}` : null, gender || null, user.email || null]
                    .filter(Boolean)
                    .join(' • ')}
                </div>
              ) : null}

              {/* Edit button below */}
              {isOwner && (
                <button style={{ ...ui.editBtn, marginTop: 8, alignSelf: 'start' }} onClick={() => navigate('/profile/edit')}>
                  <LuPencil size={14} style={{ marginRight: 6 }} />
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </section>

        {/* ===== Labels kept; no containers; sections divided by hairline ===== */}
        {sectionBlocks.length > 0 && (
          <div>
            {sectionBlocks.map((block, idx) => (
              <React.Fragment key={idx}>
                {block}
                {idx < sectionBlocks.length - 1 && <div style={ui.sectionDivider} />}
              </React.Fragment>
            ))}
          </div>
        )}
      </>
    );
  }

  // ---------- skeleton styles (used only when user == null) ----------
  const sk = {
    avatar: {
      width: 96,
      height: 96,
      borderRadius: '50%',
      background: '#F3F4F6',
      border: '4px solid #ddd',
      marginLeft: -12,
    },
    headerCol: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      paddingTop: 8,
    },
    line: {
      height: 12,
      borderRadius: 8,
      background: '#f1f5f9',
    },
    pill: {
      height: 22,
      width: 102,
      borderRadius: 999,
      background: '#eef2f7',
    },
    chip: {
      display: 'inline-block',
      height: 26,
      width: 80,
      borderRadius: 999,
      background: '#F7F8FA',
      border: '1px solid #E7E9EE',
    },
    row: {
      display: 'grid',
      gridTemplateColumns: '40px 1fr auto',
      alignItems: 'center',
      padding: '10px 0',
      marginLeft: 5,
    },
    iconDot: {
      width: 36,
      height: 36,
      borderRadius: 999,
      border: '1px solid #E7E9EE',
      background: '#F7F8FA',
    },
    divider: {
      height: 1,
      backgroundColor: '#EEF0F2',
      marginLeft: 52,
    },
    shimmerClass: 'pulse',
  };

  // ----- MAIN RENDER -----
  if (!user) {
    return (
      <div style={ui.page}>
        <div style={ui.container}>
          {/* Header skeleton */}
          <section style={ui.headerCard}>
            <div style={ui.headerRow}>
              <div style={sk.avatar} className={sk.shimmerClass} />
              <div style={sk.headerCol}>
                <div style={{ ...sk.line, width: '55%' }} className={sk.shimmerClass} />
                <div style={{ ...sk.line, width: '70%', marginTop: 8 }} className={sk.shimmerClass} />
                <div style={{ ...sk.pill, marginTop: 10 }} className={sk.shimmerClass} />
              </div>
            </div>
          </section>

          {/* About skeleton */}
          <div>
            <h2 style={ui.sectionLabel}>About</h2>
            <div style={{ ...sk.line, width: '92%', marginLeft: 5 }} className={sk.shimmerClass} />
            <div style={{ ...sk.line, width: '86%', marginLeft: 5, marginTop: 8 }} className={sk.shimmerClass} />
            <div style={{ ...sk.line, width: '78%', marginLeft: 5, marginTop: 8 }} className={sk.shimmerClass} />
          </div>
          <div style={ui.sectionDivider} />

          {/* Interests skeleton */}
          <div>
            <h2 style={ui.sectionLabel}>Interests</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginLeft: 5 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={sk.chip} className={sk.shimmerClass} />
              ))}
            </div>
          </div>
          <div style={ui.sectionDivider} />

          {/* Social skeleton */}
          <div>
            <h2 style={ui.sectionLabel}>Social</h2>

            {/* Row 1 */}
            <div style={sk.row}>
              <div style={sk.iconDot} className={sk.shimmerClass} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                <div style={{ ...sk.line, width: '30%' }} className={sk.shimmerClass} />
                <div style={{ ...sk.line, width: '45%' }} className={sk.shimmerClass} />
              </div>
              <div style={{ ...sk.pill, width: 64 }} className={sk.shimmerClass} />
            </div>
            <div style={sk.divider} />

            {/* Row 2 */}
            <div style={sk.row}>
              <div style={sk.iconDot} className={sk.shimmerClass} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                <div style={{ ...sk.line, width: '28%' }} className={sk.shimmerClass} />
                <div style={{ ...sk.line, width: '40%' }} className={sk.shimmerClass} />
              </div>
              <div style={{ ...sk.pill, width: 64 }} className={sk.shimmerClass} />
            </div>
            <div style={sk.divider} />

            {/* Row 3 */}
            <div style={sk.row}>
              <div style={sk.iconDot} className={sk.shimmerClass} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                <div style={{ ...sk.line, width: '34%' }} className={sk.shimmerClass} />
                <div style={{ ...sk.line, width: '48%' }} className={sk.shimmerClass} />
              </div>
              <div style={{ ...sk.pill, width: 64 }} className={sk.shimmerClass} />
            </div>
          </div>
        </div>

        <MinimalFooter />

        {/* Pulse keyframes scoped to this page */}
        <style>
          {`
            @keyframes pulseAnim {
              0% { opacity: .6; }
              50% { opacity: 1; }
              100% { opacity: .6; }
            }
            .pulse {
              animation: pulseAnim 1.2s ease-in-out infinite;
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={ui.page}>
      <div style={ui.container}>
        {renderReadOnlyView()}
      </div>
      <MinimalFooter />
    </div>
  );
}

/* ----------------------------------
   Premium Minimal UI — Tight Scale
---------------------------------- */
const ui = {
  // Page
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '85vh', // ensure footer sticks to bottom
    background: '#FFFFFF',
    color: '#0B1020',
    fontFamily:
      'Open Sans, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
  },
  container: {
    maxWidth: 760,
    margin: '0 auto',
    padding: '16px',
    width: '100%',
    boxSizing: 'border-box',
    flex: 1, // occupy remaining space above footer
  },

  /* ===== Top Card (tight) ===== */
  headerCard: {
    borderRadius: 12,
    background: '#FFFFFF',
    padding: 12,
    marginBottom: 14,
  },
  headerRow: {
    display: 'grid',
    gridTemplateColumns: '96px 1fr', // keep text stack within avatar height
    columnGap: 12,
    alignItems: 'center',
  },
  headerAvatar: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    objectFit: 'cover',
    background: '#F3F4F6',
    border: '4px solid #ddd',
    marginLeft: -12,
  },
  headerTextCol: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },

  // Name row
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  name: {
    margin: 0,
    fontSize: 'clamp(1.3rem, 2.2vw, 1.35rem)',
    fontWeight: 900,
    letterSpacing: '-0.005em',
    lineHeight: 1.5,
    color: '#0B1020',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    marginTop: 8,
  },

  // Age • Gender • Email line
  metaAG: {
    marginTop: 4,
    fontSize: 12.5,
    color: '#555',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.5,
  },

  editBtn: {
    background: '#FFFFFF',
    color: '#0B1020',
    border: '1px solid #E7E9EE',
    borderRadius: 10,
    padding: '6px 10px',
    fontSize: 11.5,
    fontWeight: 800,
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    marginLeft: -4,
  },

  // Location
  metaLocation: {
    marginTop: 6,
    fontSize: 12,
    color: '#6B7280',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // Labels (kept)
  sectionLabel: {
    margin: '14px 2px 8px',
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#555',
    marginLeft: 5, // aligns after icon
  },

  // Hairline divider between sections
  sectionDivider: {
    height: 1,
    backgroundColor: '#EEF0F2',
    margin: '12px 0',
  },

  // Text
  bodyText: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.6,
    color: '#2E3238',
    marginLeft: 5, // aligns after icon
  },

  // Interests (chips)
  chipsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginLeft: 5, // aligns after icon
  },
  chip: {
    display: 'inline-block',
    padding: '6px 10px',
    borderRadius: 999,
    background: '#F7F8FA',
    color: '#0B1020',
    border: '1px solid #E7E9EE',
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },

  // Social list rows (no wrapping container)
  listRow: {
    display: 'grid',
    gridTemplateColumns: '40px 1fr auto',
    alignItems: 'center',
    padding: '10px 0',
    textDecoration: 'none',
    color: '#0B1020',
    marginLeft: 5, // aligns after icon
  },
  rowDivider: {
    height: 1,
    backgroundColor: '#EEF0F2',
    marginLeft: 52, // aligns after icon
  },
  iconDot: {
    width: 36,
    height: 36,
    borderRadius: 999,
    display: 'grid',
    placeItems: 'center',
    border: '1px solid #E7E9EE',
    background: '#F7F8FA',
    fontSize: 16,
    color: '#111',
  },
  rowText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  rowTitle: {
    fontSize: 13,
    fontWeight: 900,
    color: '#0B1020',
    lineHeight: 1.2,
    marginLeft: 6,

  },
  rowSub: {
    marginTop: 2,
    fontSize: 12.5,
    color: '#4A5568',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginLeft: 6,

  },
  rowMeta: {
    fontSize: 12,
    color: '#6B7280',
    marginLeft: 12,

    // >>> pill styling for "Open"
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 10px',
    borderRadius: 999,
    background: '#F7F8FA',
    border: '1px solid #E7E9EE',
    lineHeight: 1,
  },

  // Inline nudges (no containers)
  inlineNudge: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 13.5,
    color: '#4A5568',
    marginLeft: 4,
    marginRight:8,
  },
  inlineBtn: {
    background: '#ddd',
    color: '#555',
    border: '1px solid #ddd',
    borderRadius: 10,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 900,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    marginLeft: 8,
    marginBottom: 8,

  },
};
